import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  createRentalPortalListing,
  updateRentalPortalListing,
} from '../../../../../../api'
import { isFormFieldsEqual } from '../../../../../../utils/formHelpers'
import {
  getHasListingLivePublications,
  getIsListingChangedConflictError,
  getListingConflictVersion,
  getListingEditConflictNotification,
  getListingResyncDisclosureModal,
  getListingUnitLabel,
  LISTING_EDIT_CONFLICT_NOTIFICATION_ID,
} from '../../../../../RentalPortalListing'
import { LISTING_FORM_FIELD_NAME_TIMING } from '../../RentalPortalListingsForm.constants'
import {
  getHasChangedListingAdMaterial,
  getHasListingPortalsToPublish,
  getPayload,
} from '../../RentalPortalListingsForm.utils'
import { useRentalPortalListingsFormPhotos } from '../useRentalPortalListingsFormPhotos'
import { useRentalPortalListingsFormPublish } from '../useRentalPortalListingsFormPublish'

import {
  getListingFormSuccessTiming,
  setListingFormSaveError,
} from './useRentalPortalListingsFormSubmit.utils'

export function useRentalPortalListingsFormSubmit({
  formInitialValues,
  isEditMode,
  listing,
  listingId,
  savedListingId,
  setModal,
  onClose,
  onCloseModal,
  onCreateRecord,
  onReloadListing,
  onRemoveNotification,
  onSetNotification,
  onSuccess,
}) {
  const { t } = useTranslation()

  const recordVersionRef = useRef(null)

  function getRecordVersion() {
    return recordVersionRef.current ?? listing?.version
  }

  function setRecordVersion(version) {
    recordVersionRef.current = version
  }

  const { handleDeletePhoto, resetSavedPhotos, saveListingPhotos } =
    useRentalPortalListingsFormPhotos({
      formInitialValues,
      savedListingId,
      onChangeVersion: setRecordVersion,
    })

  const { publishListing, saveListingTiming } =
    useRentalPortalListingsFormPublish({
      getRecordVersion,
      listingId,
      onChangeVersion: setRecordVersion,
    })

  async function saveRecord(values, version) {
    const payload = getPayload(values)

    const { data } = await (savedListingId
      ? updateRentalPortalListing({
          data: payload,
          id: savedListingId,
          version,
        })
      : createRentalPortalListing(payload))

    return data
  }

  async function saveListingRecord({ formikHelpers, values, version }) {
    const { setErrors, setSubmitting } = formikHelpers

    try {
      const record = await saveRecord(values, version)

      setRecordVersion(record.version)

      if (!isEditMode) {
        if (onCreateRecord) {
          onCreateRecord(record)
        }
      }

      return record
    } catch (error) {
      if (getIsListingChangedConflictError(error)) {
        handleSaveConflict({ error, formikHelpers, values })
      } else {
        setListingFormSaveError({ error, setErrors, setSubmitting, t })
      }

      return null
    }
  }

  async function submitListing(values, formikHelpers, version) {
    const savedRecord = await saveListingRecord({
      formikHelpers,
      values,
      version,
    })

    if (
      !savedRecord ||
      !(await saveListingPhotos({ formikHelpers, record: savedRecord, values }))
    ) {
      return
    }

    const hasPortalsToPublish = getHasListingPortalsToPublish({
      listingId,
      timing: values[LISTING_FORM_FIELD_NAME_TIMING],
      values,
    })

    const record = await saveListingTiming({
      formikHelpers,
      hasPortalsToPublish,
      record: savedRecord,
      values,
    })

    if (
      record &&
      (!hasPortalsToPublish ||
        (await publishListing({ formikHelpers, record, values })))
    ) {
      onSuccess?.(record, getListingFormSuccessTiming({ listingId, values }))
    }
  }

  function handleSaveConflict({ error, formikHelpers, values }) {
    function handleClickReload() {
      onRemoveNotification(LISTING_EDIT_CONFLICT_NOTIFICATION_ID)

      setRecordVersion(null)

      resetSavedPhotos()

      formikHelpers.resetForm()

      onReloadListing()
    }

    function handleClickOverwrite() {
      onRemoveNotification(LISTING_EDIT_CONFLICT_NOTIFICATION_ID)

      formikHelpers.setSubmitting(true)

      submitListing(values, formikHelpers, getListingConflictVersion(error))
    }

    formikHelpers.setSubmitting(false)

    onSetNotification(
      getListingEditConflictNotification({
        error,
        t,
        onClickOverwrite: handleClickOverwrite,
        onClickReload: handleClickReload,
      })
    )
  }

  function handleDismissResyncDisclosure(setSubmitting) {
    onCloseModal()
    setSubmitting(false)
  }

  function handleSubmit(values, formikHelpers) {
    if (
      isEditMode &&
      isFormFieldsEqual({ initialValues: formInitialValues, values })
    ) {
      formikHelpers.setSubmitting(false)
      onClose()
    } else if (
      listingId &&
      getHasListingLivePublications(listing) &&
      getHasChangedListingAdMaterial({
        initialValues: formInitialValues,
        values,
      })
    ) {
      setModal(
        getListingResyncDisclosureModal({
          listingName: getListingUnitLabel({ listing, t }),
          t,
          onDismiss: () =>
            handleDismissResyncDisclosure(formikHelpers.setSubmitting),
          onSubmit: () => {
            onCloseModal()
            submitListing(values, formikHelpers, getRecordVersion())
          },
        })
      )
    } else {
      submitListing(values, formikHelpers, getRecordVersion())
    }
  }

  return { handleDeletePhoto, handleSubmit }
}
